import { useTranslation } from 'react-i18next';
import { Button, InputField, useToast } from '@hostfully/ui-components';
import {
  CredentialRow,
  CredentialsWarning,
  PartnerTokenExchangeSubtitle,
  PartnerTokenExchangeTitle,
} from './PartnerTokenExchange.styles';
import { FormProvider } from '@/components/forms/form/Form';
import type { OAuthClientCredentials } from '@/services/partner-token-exchange.types';

interface PartnerTokenExchangeCredentialsProps {
  credentials: OAuthClientCredentials;
}

const credentialFields = ['clientId', 'clientSecret'] as const;

/**
 * The client secret is hashed server-side, so this is the only time it can be
 * shown. Credentials live in component state only and are never persisted.
 */
const PartnerTokenExchangeCredentials = ({
  credentials,
}: PartnerTokenExchangeCredentialsProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const copyToClipboard = async (field: (typeof credentialFields)[number]) => {
    try {
      await navigator.clipboard.writeText(credentials[field]);
      toast.success({
        title: t('pagePartnerTokenExchange.copied', {
          context: field,
        }),
      });
    } catch {
      toast.error({ title: t('pagePartnerTokenExchange.copyFailed') });
    }
  };

  return (
    <div data-testid="partner-credentials">
      <PartnerTokenExchangeTitle data-testid="partner-credentials-title">
        {t('pagePartnerTokenExchange.credentialsTitle')}
      </PartnerTokenExchangeTitle>
      <PartnerTokenExchangeSubtitle data-testid="partner-credentials-subtitle">
        {t('pagePartnerTokenExchange.credentialsSubtitle')}
      </PartnerTokenExchangeSubtitle>
      <CredentialsWarning
        role="alert"
        data-testid="partner-credentials-warning"
      >
        <strong>{t('pagePartnerTokenExchange.credentialsWarningTitle')}</strong>{' '}
        {t('pagePartnerTokenExchange.credentialsWarning')}
      </CredentialsWarning>
      <FormProvider defaultValues={credentials}>
        {credentialFields.map((field) => (
          <CredentialRow key={field}>
            <InputField
              label={t('pagePartnerTokenExchange.credentialLabel', {
                context: field,
              })}
              name={field}
              readOnly
              spellCheck={false}
            />
            <Button
              data-testid={`copy-${field}-button`}
              variant="primaryOutline"
              size="large"
              onClick={() => copyToClipboard(field)}
            >
              {t('pagePartnerTokenExchange.copy')}
            </Button>
          </CredentialRow>
        ))}
      </FormProvider>
    </div>
  );
};

export default PartnerTokenExchangeCredentials;
